// hooks/usePrefetch.js
import { useRef, useCallback } from 'react'

/**
 * Hook pour prefetch des données avec cache
 */
export function usePrefetch(fetchFn, cacheKey = 'default') {
  const cacheRef = useRef(new Map())
  const prefetchingRef = useRef(new Set())

  const prefetch = useCallback(async (id, options = {}) => {
    const { force = false, delay = 0 } = options

    // Skip si déjà en cache et pas force
    if (!force && cacheRef.current.has(id)) {
      return cacheRef.current.get(id)
    }

    // Skip si déjà en cours de prefetch
    if (prefetchingRef.current.has(id)) {
      return
    }

    // Ajouter au set de prefetch
    prefetchingRef.current.add(id)

    // Optionnel: delay avant prefetch (pour debounce hover)
    if (delay > 0) {
      await new Promise(resolve => setTimeout(resolve, delay))
    }

    try {
      const data = await fetchFn(id)
      cacheRef.current.set(id, data)
      return data
    } catch (err) {
      console.warn(`Prefetch failed for ${id}:`, err)
    } finally {
      prefetchingRef.current.delete(id)
    }
  }, [fetchFn])

  const getFromCache = useCallback((id) => {
    return cacheRef.current.get(id)
  }, [])

  const clearCache = useCallback(() => {
    cacheRef.current.clear()
  }, [])

  const invalidate = useCallback((id) => {
    cacheRef.current.delete(id)
  }, [])

  return {
    prefetch,
    getFromCache,
    clearCache,
    invalidate,
  }
}
